import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// 应用状态管理
export const useAppStore = defineStore('app', () => {
  // 状态
  const currentModule = ref('ai-advisor')
  const units = ref({
    volume: 'L',
    length: 'cm',
    alk: 'dKH',
    temperature: 'celsius'
  })
  const calculationHistory = ref<any[]>([])
  const userPreferences = ref({
    theme: 'default',
    language: 'zh-CN'
  })
  const translations = ref<Record<string, any>>({})

  // 计算属性
  const hasHistory = computed(() => calculationHistory.value.length > 0)

  // 动作
  const switchUnit = (unitType: string, unitValue: string) => {
    units.value[unitType as keyof typeof units.value] = unitValue
    saveUserPreferences()
  }

  const switchTheme = (theme: string) => {
    userPreferences.value.theme = theme
    saveUserPreferences()
    applyTheme(theme)
  }

  const applyTheme = (theme: string) => {
    // 移除所有主题类
    document.documentElement.classList.remove('theme-default', 'theme-dark', 'theme-glass')
    // 添加当前主题类
    document.documentElement.classList.add(`theme-${theme}`)
    // 更新body类
    document.body.className = `theme-${theme} theme-transition`
  }

  // 加载翻译文件
  const loadTranslations = async (lang: string) => {
    try {
      // 模块列表，对应拆分后的文件
      const modules = [
        'common',
        'waterManagement',
        'aiAdvisor',
        'parAnalyzer',
        'elements',
        'nutrients',
        'feeding',
        'quarantine',
        'tankLog',
        'camera',
        'dialog',
        'toast'
      ]
      
      const translationsMap: Record<string, any> = {}
      
      // 并行加载所有模块
      await Promise.all(
        modules.map(async (module) => {
          try {
            const data = await import(`../locales/${lang}/${module}.json`)
            translationsMap[module] = data.default || data
          } catch (err) {
            console.warn(`Failed to load translation module ${module} for ${lang}:`, err)
            // 如果模块缺失，设置为空对象
            translationsMap[module] = {}
          }
        })
      )
      
      // 合并为一个对象，保持与原始JSON相同的结构
      translations.value = translationsMap
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error)
      // 回退到中文
      if (lang !== 'zh-CN') {
        await loadTranslations('zh-CN')
      }
    }
  }

  // 切换语言
  const switchLanguage = async (lang: string) => {
    userPreferences.value.language = lang
    await loadTranslations(lang)
    saveUserPreferences()
  }

  const saveToHistory = (module: string, data: any) => {
    const timestamp = new Date().toISOString()
    calculationHistory.value.unshift({
      module,
      data,
      timestamp
    })
    
    // 只保留最近10条记录
    if (calculationHistory.value.length > 10) {
      calculationHistory.value.pop()
    }
    
    localStorage.setItem('reefCalcHistory', JSON.stringify(calculationHistory.value))
  }

  const clearHistory = () => {
    calculationHistory.value = []
    localStorage.removeItem('reefCalcHistory')
  }

  const saveUserPreferences = () => {
    const preferences = {
      currentModule: currentModule.value,
      units: units.value,
      userPreferences: userPreferences.value
    }
    localStorage.setItem('reefCalcPreferences', JSON.stringify(preferences))
  }

  const loadUserPreferences = async () => {
    const saved = localStorage.getItem('reefCalcPreferences')
    if (saved) {
      const preferences = JSON.parse(saved)
      currentModule.value = preferences.currentModule || 'ai-advisor'
      units.value = preferences.units || units.value
      userPreferences.value = preferences.userPreferences || userPreferences.value
      // 应用保存的主题
      applyTheme(userPreferences.value.theme)
      // 加载翻译
      await loadTranslations(userPreferences.value.language)
    } else {
      // 默认应用默认主题
      applyTheme('default')
      // 加载默认翻译
      await loadTranslations('zh-CN')
    }
  }

  const loadHistory = () => {
    const saved = localStorage.getItem('reefCalcHistory')
    if (saved) {
      calculationHistory.value = JSON.parse(saved)
    }
  }

  // 初始化
  const initialize = async () => {
    await loadUserPreferences()
    loadHistory()
  }

  return {
    // 状态
    currentModule,
    units,
    calculationHistory,
    userPreferences,
    translations,
    
    // 计算属性
    hasHistory,
    
    // 动作
    switchUnit,
    switchTheme,
    applyTheme,
    saveToHistory,
    clearHistory,
    saveUserPreferences,
    loadUserPreferences,
    loadHistory,
    initialize,
    switchLanguage,
    loadTranslations
  }
})